// queryCache.js — the Desk's layered result cache (B1214 steps 3–5, owner 2026-09-05).
//
//   memory  →  IndexedDB  →  network,  stale-while-revalidate.
//
// THE USER-VISIBLE INVARIANT: if the Desk has already seen usable data for this exact view, the user
// never waits to see it again. Network refresh happens BEHIND the rendered experience, not instead of it.
//
// Two stores, on purpose (owner rule 1 / 7):
//   queries   keyed by the CANONICAL query key (src/lib/queryKey.js — never a second interpretation of
//             the filters): ordered listing ids, the totals/shelf/cursor metadata, fetched_at, version.
//             Small: a page is ~100 ids + a few numbers, not 850 KB of rows.
//   entities  keyed by listing id: the card row itself. A property that appears in ten searches is
//             stored once and rehydrated into each; a NEWER observation overwrites the entity, so every
//             cached search that references it shows the newer row on its next paint.
//
// Two rules keep the shared entity honest (search/filter audit 2026-10-23, Desk §2):
//   • MATCH EVIDENCE BELONGS TO THE QUERY. `matched_filter` / `matched_street` answer "why is this row in
//     THIS search", so they are kept on the query entry (`witness`), never on the shared entity. Before,
//     reading street A's page rehydrated the row street B wrote last, with street B's match on it — and
//     the card picks its face advert and its address line from that evidence.
//   • ENTITY FACTS ARE ORDERED BY WHEN THEY WERE OBSERVED. Each entity carries `at`, the fetch time of the
//     answer that delivered it. An older row stored late never replaces a newer one, and a newer SUMMARY
//     row (a light page) refreshes the fields it carries over an older FULL row instead of being refused,
//     so a fresh query is not painted with an old price. Memory and IndexedDB share one rule (mergeEntity).
//
// IndexedDB, not localStorage: deskCache.js (2026-08-09) already does stale-first from localStorage and
// spends most of its code on the ~5 MB quota. IndexedDB holds hundreds of MB, is async (never blocks a
// paint), and stores structured values without a JSON round-trip. deskCache.js stays for the tiny
// protected count maps; this file owns query results and entities.
//
// Failure model: every layer fails OPEN. No IndexedDB (private window, a browser that throws on
// access, a quota error) → memory only; no memory hit → the network, exactly as today. Nothing here
// may ever turn a miss into an error the feed sees.
//
// Versioning (rule 12): QUERY_KEY_VERSION is inside every key, and CACHE_SCHEMA is the IndexedDB
// version — bump it when the stored shape changes and the old stores are dropped on open.
import { QUERY_KEY_VERSION } from '../lib/queryKey.js'

// 2 (2026-10-23): entities carry `at` and no match evidence; queries carry `witness`. Old stores are dropped.
export const CACHE_SCHEMA = 2
const DB_NAME = 'sntv_desk_cache'
const MEM_MAX_QUERIES = 200          // LRU over query entries in memory
const MEM_MAX_ENTITIES = 5000        // ~5000 card rows ≈ 9 MB at 1.8 KB — a session's worth
export const DEFAULT_MAX_AGE_MS = 6 * 60 * 60 * 1000   // entries older than this are not painted stale (6 h)

// ── memory layer ────────────────────────────────────────────────────────────────────────────────
const memQueries = new Map()   // key → entry (insertion order = LRU order; re-set on read)
const memEntities = new Map()  // listing_id → { row, at }  (at = when the row was observed; see mergeEntity)
const touch = (map, k, v, max) => { map.delete(k); map.set(k, v); while (map.size > max) map.delete(map.keys().next().value) }

// ── IndexedDB layer (injectable, so tests run under node with a fake) ───────────────────────────
let idbFactory = () => (typeof globalThis.indexedDB !== 'undefined' ? globalThis.indexedDB : null)
export function _setIdbFactory(f) { idbFactory = f; dbPromise = null }
let dbPromise = null
function openDb() {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve) => {
    let idb = null
    try { idb = idbFactory() } catch { idb = null }
    if (!idb) return resolve(null)
    let req
    try { req = idb.open(DB_NAME, CACHE_SCHEMA) } catch { return resolve(null) }
    req.onupgradeneeded = () => {
      const db = req.result
      for (const name of [...db.objectStoreNames]) db.deleteObjectStore(name)   // a schema bump drops everything
      db.createObjectStore('queries', { keyPath: 'key' }).createIndex('fetched_at', 'fetched_at')
      db.createObjectStore('entities', { keyPath: 'id' })
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => resolve(null)
    req.onblocked = () => resolve(null)
  })
  return dbPromise
}
const idbGet = (db, store, key) => new Promise((resolve) => {
  try { const r = db.transaction(store, 'readonly').objectStore(store).get(key); r.onsuccess = () => resolve(r.result ?? null); r.onerror = () => resolve(null) }
  catch { resolve(null) }
})
const idbGetMany = (db, store, keys) => Promise.all(keys.map(k => idbGet(db, store, k)))
const idbPutMany = (db, store, values) => new Promise((resolve) => {
  try {
    const tx = db.transaction(store, 'readwrite'); const os = tx.objectStore(store)
    for (const v of values) os.put(v)
    tx.oncomplete = () => resolve(true); tx.onerror = () => resolve(false); tx.onabort = () => resolve(false)
  } catch { resolve(false) }
})

export const metrics = { hit_memory: 0, hit_indexeddb: 0, miss: 0 }   // ticked inside readQuery itself

// ── entity facts vs query evidence ──────────────────────────────────────────────────────────────
// The fields that describe a row's relation to ONE question rather than the row. They live on the query entry.
export const QUERY_OWNED_FIELDS = ['matched_filter', 'matched_street']
function splitRow(l) {
  let witness = null, row = l
  for (const k of QUERY_OWNED_FIELDS) {
    if (!(k in l)) continue
    if (row === l) row = { ...l }
    ;(witness ||= {})[k] = l[k]
    delete row[k]
  }
  return { row, witness }
}
const dropFlag = (row) => { const { __summary, ...rest } = row; return rest }
/**
 * cur = { row, at } held for this id (or null); `row` was observed at `at`. Returns the record to keep,
 * or null when `cur` already holds everything newer. One rule for memory and IndexedDB:
 *   newer, or the same observation → it wins. A summary replaces only the fields it carries, so the full
 *     detail already held survives. At an equal time a summary never displaces a full row (B1214 step 7).
 *   older → refused, except that an older FULL row may fill in the detail a newer summary lacks; the
 *     summary's own, newer fields stay on top.
 * Known limit, by choice: once a newer summary is laid over a full row, a full row older than that summary
 * is refused even if it is newer than the detail underneath. The card still shows the newest shared facts,
 * and the next fresh full row replaces everything.
 */
export function mergeEntity(cur, row, at) {
  if (!cur || !cur.row) return { row, at }
  const summary = !!row.__summary, curSummary = !!cur.row.__summary
  const curAt = Number.isFinite(cur.at) ? cur.at : 0
  if (at > curAt || (at === curAt && !(summary && !curSummary))) {
    return summary && !curSummary ? { row: { ...cur.row, ...dropFlag(row) }, at } : { row, at }
  }
  if (!summary && curSummary) return { row: { ...row, ...dropFlag(cur.row) }, at: curAt }
  return null
}
const memPutEntity = (id, rec) => {
  const m = memEntities.get(id)
  if (!m || !(m.at > rec.at)) touch(memEntities, id, rec, MEM_MAX_ENTITIES)
}
// One readwrite transaction for the read-compare-put: IndexedDB runs transactions on a store one after
// another, so two pages stored at the same moment merge in turn instead of both reading the old record
// and the later put winning.
const idbMergeEntities = (db, rows, at) => new Promise((resolve) => {
  try {
    const tx = db.transaction('entities', 'readwrite'); const os = tx.objectStore('entities')
    for (const row of rows) {
      const r = os.get(row.listing_id)
      r.onsuccess = () => {
        const cur = r.result && r.result.row ? { row: r.result.row, at: r.result.at } : null
        const next = mergeEntity(cur, row, at)
        if (next) os.put({ id: row.listing_id, row: next.row, at: next.at })
        // Memory follows what IndexedDB settled on: an entity evicted from memory must not come back older.
        const final = next || cur
        if (final) memPutEntity(row.listing_id, { row: final.row, at: Number.isFinite(final.at) ? final.at : 0 })
      }
    }
    tx.oncomplete = () => resolve(true); tx.onerror = () => resolve(false); tx.onabort = () => resolve(false)
  } catch { resolve(false) }
})

// ── the API ─────────────────────────────────────────────────────────────────────────────────────
/** Store a page: the ids, metadata and match evidence under the query key, the rows under their ids. */
// `fetchedAt` (2026-09-24): a caller RE-STORING an answer it already had (the Desk's chain-
// completion write) passes the ORIGINAL fetch time, because freshness (lib/deskFreshness.js) is
// judged on fetched_at and re-stamping it would make an old answer look new. Default: now.
// The same time versions the entities (mergeEntity), so such a re-store cannot undo rows a later
// page has refreshed since.
export async function writeQuery(key, { listings = [], ...meta } = {}, { fetchedAt } = {}) {
  if (!key || !key.startsWith(QUERY_KEY_VERSION + '|')) return false   // a key from another interpretation is refused
  const at = Number.isFinite(fetchedAt) && fetchedAt > 0 ? fetchedAt : Date.now()
  const ids = [], rows = []
  let witness = null
  for (const l of listings) {
    if (!l || !l.listing_id) continue
    const split = splitRow(l)
    ids.push(l.listing_id); rows.push(split.row)
    if (split.witness) (witness ||= {})[l.listing_id] = split.witness
  }
  const entry = { key, ids, meta, witness, fetched_at: at, version: QUERY_KEY_VERSION }
  touch(memQueries, key, entry, MEM_MAX_QUERIES)
  for (const row of rows) {
    const next = mergeEntity(memEntities.get(row.listing_id) || null, row, at)
    if (next) touch(memEntities, row.listing_id, next, MEM_MAX_ENTITIES)
  }
  const db = await openDb()
  if (!db) return true
  await idbMergeEntities(db, rows, at)
  await idbPutMany(db, 'queries', [entry])
  return true
}

/**
 * Read a page: memory first, then IndexedDB. Returns { listings, ...meta, fetched_at, source } or null.
 * A hit whose entities are incomplete (an entity evicted or never stored) is a miss — a page must
 * never paint with holes where cards should be.
 */
export async function readQuery(key, { maxAgeMs = DEFAULT_MAX_AGE_MS } = {}) {
  if (!key) return null
  let entry = memQueries.get(key), source = 'memory'
  if (entry) memQueries.delete(key), memQueries.set(key, entry)
  const db = entry ? null : await openDb()
  if (!entry && db) { entry = await idbGet(db, 'queries', key); source = 'indexeddb' }
  if (!entry || entry.version !== QUERY_KEY_VERSION) { metrics.miss++; return null }
  if (Date.now() - entry.fetched_at > maxAgeMs) { metrics.miss++; return null }
  const rows = new Array(entry.ids.length)
  const missing = []
  entry.ids.forEach((id, i) => { const m = memEntities.get(id); if (m) rows[i] = m.row; else missing.push([id, i]) })
  if (missing.length) {
    const dbe = db || await openDb()
    if (!dbe) return null
    const got = await idbGetMany(dbe, 'entities', missing.map(([id]) => id))
    got.forEach((e, j) => { if (e && e.row) { rows[missing[j][1]] = e.row; memPutEntity(e.id, { row: e.row, at: Number.isFinite(e.at) ? e.at : 0 }) } })
    if (rows.some(r => r == null)) return null
  }
  if (source === 'indexeddb') touch(memQueries, key, entry, MEM_MAX_QUERIES)
  if (source === 'memory') metrics.hit_memory++; else metrics.hit_indexeddb++
  // This query's own match evidence goes back on its rows, and only this query's (see the header).
  const w = entry.witness || null
  const listings = w ? rows.map((r, i) => (w[entry.ids[i]] ? { ...r, ...w[entry.ids[i]] } : r)) : rows
  return { listings, ...entry.meta, fetched_at: entry.fetched_at, source }
}

/** One entity's shared facts, from memory or IndexedDB (no query's match evidence). */
export async function readEntity(id) {
  const m = memEntities.get(id); if (m) return m.row
  const db = await openDb(); if (!db) return null
  const e = await idbGet(db, 'entities', id)
  if (e && e.row) memPutEntity(id, { row: e.row, at: Number.isFinite(e.at) ? e.at : 0 })
  return e ? e.row : null
}

/** Is a page already cached (in either layer)? Cheap: no entity rehydration. */
export async function hasQuery(key) {
  if (memQueries.has(key)) return true
  const db = await openDb(); if (!db) return false
  return !!(await idbGet(db, 'queries', key))
}

// ── in-flight dedupe: one query key, at most one active fetch (rule 8) ──────────────────────────
const inflight = new Map()
export function dedupeFetch(key, run) {
  const hit = inflight.get(key); if (hit) return hit
  const p = Promise.resolve().then(run).finally(() => { if (inflight.get(key) === p) inflight.delete(key) })
  inflight.set(key, p)
  return p
}
export function _inflightCount() { return inflight.size }

// ── stale-while-revalidate (rule 2) ─────────────────────────────────────────────────────────────
/**
 * swr(key, fetcher, { onStale, onFresh }) — paints the cached page immediately when one exists
 * (onStale), always fetches (deduped per key), stores the fresh page and reports it (onFresh).
 * Returns the fresh promise. Consumers decide how to merge without resetting scroll/page.
 */
export function swr(key, fetcher, { onStale, onFresh, onError, maxAgeMs } = {}) {
  const stalePromise = readQuery(key, { maxAgeMs }).then(hit => { if (hit && onStale) onStale(hit); return hit }).catch(() => null)
  const freshPromise = dedupeFetch(key, fetcher).then(async (fresh) => {
    await stalePromise                       // never let a stale paint land AFTER the fresh one
    if (fresh) { writeQuery(key, fresh).catch(() => {}); if (onFresh) onFresh(fresh) }
    return fresh
  }, (e) => { if (onError) onError(e); throw e })
  return freshPromise
}

// ── instrumentation (rule 11) ───────────────────────────────────────────────────────────────────
export async function readQueryMetered(key, opts) { return readQuery(key, opts) }
/** Sign-out: drop what is held in memory and close the DB handle so its deletion is not blocked by us. */
export function clearMemoryCache() {
  memQueries.clear(); memEntities.clear(); inflight.clear()
  const p = dbPromise; dbPromise = null
  if (p) p.then(db => { try { db?.close?.() } catch { /* already closed */ } }).catch(() => {})
}
export function _resetForTests() { memQueries.clear(); memEntities.clear(); inflight.clear(); dbPromise = null; metrics.hit_memory = metrics.hit_indexeddb = metrics.miss = 0 }
